import { useState } from 'react'
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    FormGroup,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material'
import { Add, Delete } from '@mui/icons-material'

import {
    useCreateManagementKeyMutation,
    useListManagementKeysQuery,
    useRevokeManagementKeyMutation,
} from '@/apis/opsApi'
import { MANAGEMENT_SCOPES, type ManagementKey, type ManagementScope } from '@/apis/opsTypes'
import { v2ErrorMessage } from '@/apis/v2Mappers'
import { ConfirmDialog } from '@/components/shared/ConfirmDialog'
import { CopyButton } from '@/components/shared/CopyButton'
import { ReportStatus, fmtDateTime } from '@/modules/reports/common'

const SCOPE_HELP: Record<ManagementScope, string> = {
    'projects:read': 'List and read projects',
    'projects:write': 'Create, update, suspend projects and manage their config',
    'keys:write': "Mint and revoke projects' pk / sk keys (ingest, server, write)",
    'analytics:delegate':
        "Also mint keys that can read analytics ('read' scope). Off unless you need it: the management key itself still never reads data",
}

/** 403 from the API: the signed-in user is not an organization owner. */
function isForbidden(err: unknown): boolean {
    return (err as { status?: unknown } | undefined)?.status === 403
}

/**
 * v2 `/management-keys` (organization owners): `yom_mk_` keys for partner provisioning.
 * They never read analytics and ingest never accepts them (docs/v2-partner-api.md §1).
 */
export function ManagementKeysCard() {
    const { data = [], isLoading, error } = useListManagementKeysQuery()
    const [createKey, createState] = useCreateManagementKeyMutation()
    const [revokeKey] = useRevokeManagementKeyMutation()
    const [open, setOpen] = useState(false)
    const [name, setName] = useState('')
    const [scopes, setScopes] = useState<ManagementScope[]>(['projects:read', 'projects:write'])
    const [created, setCreated] = useState<string | null>(null)
    const [toRevoke, setToRevoke] = useState<ManagementKey | null>(null)

    const create = async () => {
        try {
            const r = await createKey({
                name: name.trim() || 'management key',
                scopes,
                expires_at: null,
            }).unwrap()
            setCreated(r.key)
            setOpen(false)
            setName('')
        } catch {
            // shown from createState.error
        }
    }

    const active = data.filter(k => !k.revoked_at)
    return (
        <Card sx={{ mt: 3 }}>
            <CardContent>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 1,
                    }}
                >
                    <Typography variant="h6">Management keys</Typography>
                    {!isForbidden(error) && (
                        <Button size="small" startIcon={<Add />} onClick={() => setOpen(true)}>
                            New management key
                        </Button>
                    )}
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    For partner platforms that create projects and keys through the API. They cannot
                    read analytics or send events.
                </Typography>
                {created && (
                    <Alert severity="success" sx={{ mb: 2 }} onClose={() => setCreated(null)}>
                        <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                            Copy the key now; it is not shown again.
                        </Typography>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                fontFamily: 'monospace',
                                wordBreak: 'break-all',
                            }}
                        >
                            {created}
                            <CopyButton text={created} />
                        </Box>
                    </Alert>
                )}
                {isForbidden(error) ? (
                    <Alert severity="info">Only organization owners can manage these keys.</Alert>
                ) : (
                    <ReportStatus
                        isLoading={isLoading}
                        error={error}
                        isEmpty={active.length === 0}
                        emptyTitle="No management keys"
                    >
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Key</TableCell>
                                    <TableCell>Scopes</TableCell>
                                    <TableCell>Last used</TableCell>
                                    <TableCell />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {active.map(k => (
                                    <TableRow key={k.id}>
                                        <TableCell>{k.name}</TableCell>
                                        <TableCell sx={{ fontFamily: 'monospace' }}>
                                            {k.key_prefix}…
                                        </TableCell>
                                        <TableCell>
                                            {k.scopes.map(s => (
                                                <Chip
                                                    key={s}
                                                    size="small"
                                                    label={s}
                                                    sx={{ mr: 0.5 }}
                                                />
                                            ))}
                                        </TableCell>
                                        <TableCell>
                                            {k.last_used_at ? fmtDateTime(k.last_used_at) : 'never'}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Tooltip title="Revoke">
                                                <IconButton
                                                    size="small"
                                                    aria-label={`Revoke ${k.name}`}
                                                    onClick={() => setToRevoke(k)}
                                                >
                                                    <Delete fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </ReportStatus>
                )}
            </CardContent>
            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>New management key</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {createState.error && (
                        <Alert severity="error">{v2ErrorMessage(createState.error)}</Alert>
                    )}
                    <TextField
                        size="small"
                        label="Name"
                        placeholder="provisioning"
                        value={name}
                        onChange={e => setName(e.target.value)}
                        sx={{ mt: 1 }}
                    />
                    <FormGroup>
                        {MANAGEMENT_SCOPES.map(s => (
                            <FormControlLabel
                                key={s}
                                control={
                                    <Checkbox
                                        checked={scopes.includes(s)}
                                        onChange={e =>
                                            setScopes(
                                                e.target.checked
                                                    ? [...scopes, s]
                                                    : scopes.filter(x => x !== s),
                                            )
                                        }
                                    />
                                }
                                label={`${s}: ${SCOPE_HELP[s]}`}
                            />
                        ))}
                    </FormGroup>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        disabled={scopes.length === 0 || createState.isLoading}
                        onClick={() => {
                            void create()
                        }}
                    >
                        Create
                    </Button>
                </DialogActions>
            </Dialog>
            <ConfirmDialog
                open={!!toRevoke}
                title="Revoke management key?"
                message={`Integrations using ${toRevoke?.name ?? 'this key'} stop working immediately.`}
                confirmLabel="Revoke"
                confirmColor="error"
                onConfirm={() => {
                    if (toRevoke) {
                        void revokeKey(toRevoke.id)
                    }
                    setToRevoke(null)
                }}
                onCancel={() => setToRevoke(null)}
            />
        </Card>
    )
}
